import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    Briefcase,
    CalendarDays,
    Users,
    Pencil,
    Trash2,
    EyeOff,
    Eye,
    Rocket,
    Sparkles,
    MessageCircle,
    XCircle,
    Settings as Cog,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
    listServicesByOwner,
    listEventsByOwner,
    deleteService,
    updateService,
    updateEvent,
    getAllEvents,
    isEventPast,
    getEventAttendees,
} from "@/lib/entitiesStore";
import { getJoinedEvents } from "@/lib/socialStore";
import { useSocialVersion } from "@/lib/hooks";
import InitialAvatar from "@/components/InitialAvatar";
import ServiceSettingsSheet from "@/components/ServiceSettingsSheet";

const TABS = [
    { id: "services", label: "My Services", icon: Briefcase },
    { id: "events",   label: "My Events",   icon: CalendarDays },
    { id: "joined",   label: "Joined",      icon: Users },
];

const Manage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const v = useSocialVersion();
    const [tab, setTab] = useState("services");
    const [settingsService, setSettingsService] = useState(null);

    const myServices = useMemo(() => (user ? listServicesByOwner(user.id) : []), [user, v]);
    const myEvents = useMemo(() => (user ? listEventsByOwner(user.id) : []), [user, v]);
    const joinedEvents = useMemo(() => {
        if (!user) return [];
        const ids = getJoinedEvents(user.id);
        const all = getAllEvents();
        return ids
            .map((id) => all.find((e) => e.id === id))
            .filter((e) => e && e.ownerId !== user.id);
    }, [user, v]);

    const isPremium = !!user?.premium;

    return (
        <div className="screen" data-testid="manage-screen">
            <header className="px-4 pt-[max(14px,env(safe-area-inset-top))] pb-3 border-b border-border/60 flex items-center justify-between">
                <button
                    onClick={() => navigate(-1)}
                    data-testid="manage-back"
                    className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-muted"
                >
                    <ArrowLeft size={20} />
                </button>
                <h1 className="font-display text-[16px] font-semibold">Manage</h1>
                <span className="h-10 w-10" />
            </header>

            <div className="px-4 pt-4 flex gap-2 overflow-x-auto no-scrollbar" data-testid="manage-tabs">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.id}
                            data-testid={`manage-tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={[
                                "shrink-0 h-10 px-3.5 rounded-xl text-[12.5px] font-medium inline-flex items-center justify-center gap-1.5 transition-colors",
                                tab === t.id
                                    ? "bg-primary text-primary-foreground shadow-soft"
                                    : "bg-secondary text-secondary-foreground hover:bg-muted",
                            ].join(" ")}
                        >
                            <Icon size={14} strokeWidth={2.2} />
                            {t.label}
                        </button>
                    );
                })}
            </div>

            <div className="flex-1 overflow-y-auto no-scrollbar px-4 pt-4 pb-6 space-y-3">
                {tab === "services" && (
                    myServices.length === 0
                        ? <Empty label="No services yet" ctaLabel="Create Service" onCta={() => navigate("/create/service")} />
                        : myServices.map((s) => (
                            <ServiceRow
                                key={s.id}
                                service={s}
                                isPremium={isPremium}
                                navigate={navigate}
                                onSettings={() => setSettingsService(s)}
                            />
                        ))
                )}
                {tab === "events" && (
                    myEvents.length === 0
                        ? <Empty label="No events yet" ctaLabel="Create Event" onCta={() => navigate("/create/event")} />
                        : myEvents.map((e) => (
                            <EventOwnerRow key={e.id} event={e} isPremium={isPremium} navigate={navigate} />
                        ))
                )}
                {tab === "joined" && (
                    joinedEvents.length === 0
                        ? <Empty label="You haven't joined any events" />
                        : joinedEvents.map((e) => (
                            <JoinedEventRow key={e.id} event={e} navigate={navigate} />
                        ))
                )}
            </div>

            <ServiceSettingsSheet
                open={!!settingsService}
                service={settingsService}
                isPremium={isPremium}
                onClose={() => setSettingsService(null)}
            />
        </div>
    );
};

const Row = ({ children, testId }) => (
    <div className="rounded-2xl border border-border bg-card shadow-soft p-3" data-testid={testId}>
        {children}
    </div>
);

const Avatar = ({ src, name, size = 56 }) =>
    src ? (
        <img src={src} alt="" style={{ height: size, width: size }} className="rounded-xl object-cover shrink-0" />
    ) : (
        <div className="shrink-0 rounded-xl overflow-hidden" style={{ height: size, width: size }}>
            <InitialAvatar name={name || "?"} size={size} />
        </div>
    );

const ActionBtn = ({ icon: Icon, label, onClick, danger, disabled, testId }) => (
    <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        disabled={disabled}
        className={[
            "inline-flex items-center gap-1 px-2.5 h-8 rounded-full text-[11.5px] font-medium border transition-colors",
            danger
                ? "border-destructive/30 text-destructive hover:bg-destructive/5"
                : "border-border text-foreground hover:bg-muted",
            disabled ? "opacity-50 cursor-not-allowed" : "",
        ].join(" ")}
    >
        <Icon size={12} />
        {label}
    </button>
);

const ServiceRow = ({ service, isPremium, navigate, onSettings }) => {
    const hidden = !!service.hidden;
    return (
        <Row testId={`manage-service-${service.id}`}>
            <div className="flex items-start gap-3">
                <Avatar src={service.image} name={service.name} />
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                        <span className="font-display text-[14px] font-semibold truncate">{service.name}</span>
                        {hidden && <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Hidden</span>}
                    </div>
                    <div className="text-[11.5px] text-muted-foreground truncate">{service.category}</div>
                </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
                <ActionBtn icon={Eye}    label="View"   onClick={() => navigate(`/services/${service.id}`)} testId={`svc-view-${service.id}`} />
                <ActionBtn icon={Pencil} label="Edit"   onClick={() => navigate(`/edit/service/${service.id}`)} testId={`svc-edit-${service.id}`} />
                <ActionBtn
                    icon={hidden ? Eye : EyeOff}
                    label={hidden ? "Unhide" : "Hide"}
                    onClick={() => updateService(service.id, { hidden: !hidden })}
                    testId={`svc-hide-${service.id}`}
                />
                <ActionBtn icon={Cog}    label="Settings" onClick={() => onSettings?.()} testId={`svc-settings-${service.id}`} />
                <ActionBtn
                    icon={Rocket}
                    label={isPremium ? "Boost" : "Boost (Premium)"}
                    onClick={() => isPremium ? navigate("/boost") : navigate("/premium")}
                    testId={`svc-boost-${service.id}`}
                />
                <ActionBtn
                    icon={Trash2}
                    label="Delete"
                    danger
                    onClick={() => {
                        if (window.confirm(`Delete "${service.name}"?`)) deleteService(service.id);
                    }}
                    testId={`svc-delete-${service.id}`}
                />
            </div>
        </Row>
    );
};

const EventOwnerRow = ({ event, isPremium, navigate }) => {
    const past = isEventPast(event);
    const { count, capacity } = getEventAttendees(event, false);
    const cancelled = !!event.cancelled;
    return (
        <Row testId={`manage-event-${event.id}`}>
            <div className="flex items-start gap-3">
                <Avatar src={event.image} name={event.title} />
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                        <span className="font-display text-[14px] font-semibold truncate">{event.title}</span>
                        {past && <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Past</span>}
                        {cancelled && <span className="text-[10px] uppercase tracking-wider text-destructive">Cancelled</span>}
                    </div>
                    <div className="text-[11.5px] text-muted-foreground truncate">
                        {event.date} · {capacity ? `${count}/${capacity} attending` : `${count} attending`}
                    </div>
                </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
                <ActionBtn icon={Eye}    label="View"     onClick={() => navigate(`/events/${event.id}`)} testId={`evt-view-${event.id}`} />
                <ActionBtn icon={Pencil} label="Edit"     onClick={() => navigate(`/edit/event/${event.id}`)} testId={`evt-edit-${event.id}`} />
                <ActionBtn icon={Users}  label="Attendees" onClick={() => navigate(`/events/${event.id}/chat`)} testId={`evt-attendees-${event.id}`} />
                <ActionBtn
                    icon={Rocket}
                    label={isPremium ? "Boost" : "Boost (Premium)"}
                    onClick={() => isPremium ? navigate("/boost") : navigate("/premium")}
                    testId={`evt-boost-${event.id}`}
                />
                {!cancelled && !past && (
                    <ActionBtn
                        icon={XCircle}
                        label="Cancel"
                        danger
                        onClick={() => {
                            if (window.confirm(`Cancel "${event.title}"?`)) updateEvent(event.id, { cancelled: true });
                        }}
                        testId={`evt-cancel-${event.id}`}
                    />
                )}
            </div>
        </Row>
    );
};

const JoinedEventRow = ({ event, navigate }) => {
    const past = isEventPast(event);
    return (
        <Row testId={`manage-joined-${event.id}`}>
            <div className="flex items-start gap-3">
                <Avatar src={event.image} name={event.title} />
                <div className="min-w-0 flex-1">
                    <div className="font-display text-[14px] font-semibold truncate">{event.title}</div>
                    <div className="text-[11.5px] text-muted-foreground truncate">{event.date}</div>
                </div>
                <Sparkles size={12} className="text-primary mt-1" aria-hidden />
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
                <ActionBtn icon={Eye} label="View" onClick={() => navigate(`/events/${event.id}`)} testId={`joined-view-${event.id}`} />
                {!past && (
                    <ActionBtn
                        icon={MessageCircle}
                        label="Open chat"
                        onClick={() => navigate(`/events/${event.id}/chat`)}
                        testId={`joined-chat-${event.id}`}
                    />
                )}
            </div>
        </Row>
    );
};

const Empty = ({ label, ctaLabel, onCta }) => (
    <div className="py-12 text-center" data-testid="manage-empty">
        <p className="text-[13.5px] text-muted-foreground">{label}</p>
        {ctaLabel && (
            <button
                data-testid="manage-empty-cta"
                onClick={onCta}
                className="mt-4 inline-flex items-center justify-center h-11 px-5 rounded-full text-primary-foreground font-medium text-[13px] active:scale-[0.98]"
                style={{
                    background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                    boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                }}
            >
                {ctaLabel}
            </button>
        )}
    </div>
);

export default Manage;
